"use client"

import { Controller, Control, FieldPath, FieldValues } from "react-hook-form"
import { Switch } from "@/shared/components/ui/switch"
import { Label } from "@/shared/components/ui/label"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "@/features/homeowners/components/home-details-wrapper-accordion"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const

/** Volunteers, Supporters, and Partners cannot toggle Goodwill on Home Details. */
function useCanEditGoodwill(): boolean {
  const { session } = useSession()
  return !(
    session?.role === UserRole.VOLUNTEER ||
    session?.role === UserRole.Supporter ||
    session?.role === UserRole.PARTNER
  )
}

type GoodwillToggleLabelProps<T extends FieldValues> = {
  title: string
  toggleName: FieldPath<T>
  control: Control<T>
}

export function GoodwillToggleLabel<T extends FieldValues>({
  title,
  toggleName,
  control,
}: GoodwillToggleLabelProps<T>) {
  const sectionStyle = useWorkscopeLabelStyle(false)
  const goodwillStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
  const canEditGoodwill = useCanEditGoodwill()
  return (
    <div className="flex items-center justify-between gap-2 w-full">
      <span style={sectionStyle ?? undefined}>{title}</span>
      <div className="flex items-center gap-1.5 shrink-0">
        <Label htmlFor={String(toggleName)} className="text-xs font-normal cursor-pointer" style={goodwillStyle}>
          Goodwill
        </Label>
        <Controller
          name={toggleName}
          control={control}
          render={({ field }) => (
            <Switch
              id={String(toggleName)}
              checked={!!field.value}
              onCheckedChange={field.onChange}
              disabled={!canEditGoodwill}
            />
          )}
        />
      </div>
    </div>
  )
}
